import React, { useState } from 'react';
import { ArrowRightIcon, CheckCircleIcon, MailIcon } from 'lucide-react';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
export interface GetInfoContactValues {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}
interface GetInfoEmailFormProps {
  /** Button label, e.g. "Send Me Program Info". */
  submitLabel: string;
  /** `dark` is for placement on the deep violet band. */
  tone?: 'light' | 'dark';
  /**
   * `email` asks for an address only — the lightest possible ask. `contact`
   * also collects name and phone, for pages whose whole purpose is the request.
   */
  collect?: 'email' | 'contact';
  /**
   * Confirmation copy shown in place of the fields once submitted. Only used
   * when the visitor stays on the page — i.e. when there is no onward
   * destination.
   */
  confirmation?: string;
  /** Called with the validated values before any navigation happens. */
  onSubmitted: (values: GetInfoContactValues) => void;
  /**
   * When true, the fields are replaced by the confirmation after submitting.
   * Pass false when the parent navigates the visitor onward instead.
   */
  showConfirmation?: boolean;
  /** Distinguishes the two instances on the page for label association. */
  idPrefix: string;
}
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Contact capture used in place of a navigation CTA on the /get-info/ pages.
 * Either the address alone or full contact details, depending on how much the
 * page is asking for. Without an onward destination the visitor stays put and
 * the fields are replaced in place by a confirmation.
 */
export function GetInfoEmailForm({
  submitLabel,
  tone = 'light',
  collect = 'email',
  confirmation,
  onSubmitted,
  showConfirmation = true,
  idPrefix
}: GetInfoEmailFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isDark = tone === 'dark';
  const wantsContact = collect === 'contact';
  const fieldClass = (hasError: boolean) => `w-full rounded-lg px-3 py-3 text-base outline-none transition-colors duration-150 focus:ring-2 focus:ring-offset-2 ${isDark ? 'bg-white text-gray-900 placeholder-gray-400 border border-transparent focus:ring-cta focus:ring-offset-navy-dark' : 'bg-white text-gray-900 placeholder-gray-400 border border-gray-300 focus:border-navy focus:ring-navy focus:ring-offset-white'} ${hasError ? 'border-red-500' : ''}`;
  const labelClass = `block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`;
  const clearError = (field: string) => {
    setErrors(prev => {
      if (!prev[field]) return prev;
      const next = {
        ...prev
      };
      delete next[field];
      return next;
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    const trimmedEmail = email.trim();
    if (wantsContact) {
      if (!firstName.trim()) next.firstName = 'Enter your first name.';
      if (!lastName.trim()) next.lastName = 'Enter your last name.';
    }
    if (!trimmedEmail) {
      next.email = 'Enter your email address to continue.';
    } else if (!EMAIL_PATTERN.test(trimmedEmail)) {
      next.email = 'Enter a valid email address, like you@example.com.';
    }
    if (wantsContact && stripPhone(phone).length !== 10) {
      next.phone = 'Enter a 10-digit phone number.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    if (showConfirmation) setIsSubmitted(true);
    onSubmitted({
      email: trimmedEmail,
      ...(wantsContact ? {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: stripPhone(phone)
      } : {})
    });
  };
  if (isSubmitted && confirmation) {
    return <div role="status" className={`flex items-start gap-3 rounded-lg px-4 py-4 text-left ${isDark ? 'bg-white/10 border border-white/20' : 'bg-light border border-gray-200'}`} data-id="element-389" data-mp="28b08jdg1fs3a">
        <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-600" aria-hidden="true" data-id="element-390" data-mp="1tl1adaohggnt" />
        <div data-id="element-391" data-mp="2r7i5c99qui6e">
          <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-navy'}`} data-id="element-392" data-mp="2r81gn74w28bg">
            You're on the list.
          </p>
          <p className={`mt-1 text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-393" data-mp="2h9udbr3ivorx">
            {confirmation}
          </p>
        </div>
      </div>;
  }
  const errorText = (field: string) => errors[field] ? <p id={`${idPrefix}-${field}-error`} className={`mt-1.5 text-sm font-medium ${isDark ? 'text-red-300' : 'text-red-600'}`} data-id="element-394" data-mp="10wdnjao35j85">
        {errors[field]}
      </p> : null;
  return <form onSubmit={handleSubmit} noValidate className="text-left space-y-4" data-id="element-395" data-mp="3hjlomi10nist">
      {wantsContact && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-396" data-mp="3mglrkk2kk5fp">
          <div data-id="element-397" data-mp="3r83l4n29j3qn">
            <label htmlFor={`${idPrefix}-firstName`} className={labelClass} data-id="element-398" data-mp="3r8n1w0g6fm33">
              First name
            </label>
            <input id={`${idPrefix}-firstName`} type="text" name="firstName" value={firstName} onChange={e => {
          setFirstName(e.target.value);
          clearError('firstName');
        }} autoComplete="given-name" placeholder="Alex" aria-invalid={errors.firstName ? true : undefined} aria-describedby={errors.firstName ? `${idPrefix}-firstName-error` : undefined} className={fieldClass(Boolean(errors.firstName))} data-id="element-399" data-mp="1akbkbdt6mn" />
            {errorText('firstName')}
          </div>
          <div data-id="element-400" data-mp="23rp1w852ddq2">
            <label htmlFor={`${idPrefix}-lastName`} className={labelClass} data-id="element-401" data-mp="23s8h3h3bxo0e">
              Last name
            </label>
            <input id={`${idPrefix}-lastName`} type="text" name="lastName" value={lastName} onChange={e => {
          setLastName(e.target.value);
          clearError('lastName');
        }} autoComplete="family-name" placeholder="Rivera" aria-invalid={errors.lastName ? true : undefined} aria-describedby={errors.lastName ? `${idPrefix}-lastName-error` : undefined} className={fieldClass(Boolean(errors.lastName))} data-id="element-402" data-mp="2hrzsrb6disb8" />
            {errorText('lastName')}
          </div>
        </div>}

      <div data-id="element-403" data-mp="35i3ute3wyk04">
        <label htmlFor={`${idPrefix}-email`} className={labelClass} data-id="element-404" data-mp="35e6aip5lnmen">
          Email address
        </label>
        <div className="relative" data-id="element-405" data-mp="3p085g04sgez8">
          <MailIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" data-id="element-406" data-mp="3t3xpqdefkaeg" />
          <input id={`${idPrefix}-email`} type="email" name="email" value={email} onChange={e => {
          setEmail(e.target.value);
          clearError('email');
        }} autoComplete="email" placeholder="you@example.com" aria-invalid={errors.email ? true : undefined} aria-describedby={errors.email ? `${idPrefix}-email-error` : undefined} className={`${fieldClass(Boolean(errors.email))} pl-9`} data-id="element-407" data-mp="3k2zkjrs99wua" />
        </div>
        {errorText('email')}
      </div>

      {wantsContact && <div data-id="element-408" data-mp="2rdsmsv0mlba3">
          <label htmlFor={`${idPrefix}-phone`} className={labelClass} data-id="element-409" data-mp="2rdiswwcgmjz4">
            Phone number
          </label>
          <input id={`${idPrefix}-phone`} type="tel" name="phone" value={phone} onChange={e => {
        setPhone(formatPhone(e.target.value));
        clearError('phone');
      }} autoComplete="tel" placeholder="(555)123-4567" maxLength={13} aria-invalid={errors.phone ? true : undefined} aria-describedby={errors.phone ? `${idPrefix}-phone-error` : undefined} className={fieldClass(Boolean(errors.phone))} data-id="element-410" data-mp="36tzcvbvf5h2f" />
          {errorText('phone')}
        </div>}

      <button type="submit" className="group w-full inline-flex items-center justify-center bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-8 rounded-lg text-base transition-colors duration-200 shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-cta-dark" data-id="element-411" data-mp="2a745s71cn06t">
        {submitLabel}
        <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-412" data-mp="1v31cp481k2u7" />
      </button>
    </form>;
}